import type {
  OnboardingResource,
  OnboardingResourceBehavior,
  OnboardingResourceCategory,
  OnboardingResourceRenderType,
  OnboardingResourceSourceType,
} from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { CATEGORY_OPTIONS } from "@/lib/category-labels";
import { orpc } from "@/lib/rpc";

type ResourceKind = "link" | "notion" | "todo";

type KindConfig = {
  label: string;
  behavior: OnboardingResourceBehavior;
  renderType: OnboardingResourceRenderType;
  sourceType?: OnboardingResourceSourceType;
};

const KIND_CONFIG: Record<ResourceKind, KindConfig> = {
  link: { label: "Link externo", behavior: "redirect", renderType: "rich_doc" },
  notion: {
    label: "Sincronizar do Notion",
    behavior: "composed",
    renderType: "notion_mirror",
    sourceType: "notion",
  },
  todo: { label: "To-do", behavior: "composed", renderType: "todo" },
};

type DueRule = "day_of" | "next_day" | "this_week" | "specific_date" | "no_deadline";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional section under /company where the new resource lands. Defaults to
   *  "company-root" (the standard "A Empresa" section). */
  companySectionKey?: string;
  onCreated: (resource: OnboardingResource) => void;
};

function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function CreateResourceDialog({
  open,
  onOpenChange,
  companySectionKey = "company-root",
  onCreated,
}: Props) {
  const { toast } = useToast();
  const [kind, setKind] = useState<ResourceKind>("link");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [todoDescription, setTodoDescription] = useState("");
  const [todoLink, setTodoLink] = useState("");
  const [dueRule, setDueRule] = useState<DueRule | "">("");
  const [dueDate, setDueDate] = useState("");
  const [category, setCategory] = useState<OnboardingResourceCategory | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      setKind("link");
      setTitle("");
      setUrl("");
      setTodoDescription("");
      setTodoLink("");
      setDueRule("");
      setDueDate("");
      setCategory(null);
      setSubmitted(false);
    }
  }, [open]);

  const createMutation = useMutation(orpc.onboardingResources.create.mutationOptions());

  const needsUrl = kind === "link" || kind === "notion";
  const trimmedTitle = title.trim();
  const trimmedUrl = url.trim();
  const titleError = submitted && trimmedTitle.length === 0;
  const urlError = submitted && needsUrl && !isValidHttpUrl(trimmedUrl);
  const todoLinkError =
    submitted && kind === "todo" && todoLink.trim().length > 0 && !isValidHttpUrl(todoLink.trim());
  const dueDateError =
    submitted && kind === "todo" && dueRule === "specific_date" && dueDate.trim().length === 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (trimmedTitle.length === 0) return;
    if (needsUrl && !isValidHttpUrl(trimmedUrl)) return;
    if (kind === "todo") {
      if (todoLink.trim().length > 0 && !isValidHttpUrl(todoLink.trim())) return;
      if (dueRule === "specific_date" && dueDate.trim().length === 0) return;
    }

    const config = KIND_CONFIG[kind];
    const disciplines = ["company"];

    const contentBlocks =
      kind === "todo"
        ? [
            {
              type: "todo" as const,
              description: todoDescription.trim() || null,
              link: todoLink.trim() || null,
              dueRule: dueRule === "specific_date" || dueRule === "" ? null : dueRule,
              dueDate: dueRule === "specific_date" ? dueDate.trim() || null : null,
            },
          ]
        : undefined;

    try {
      const result = await createMutation.mutateAsync({
        title: trimmedTitle,
        behavior: config.behavior,
        renderType: config.renderType,
        sourceUrl: needsUrl ? trimmedUrl : null,
        sourceRefs:
          kind === "notion" && config.sourceType
            ? [{ url: trimmedUrl, type: config.sourceType }]
            : undefined,
        contentBlocks,
        scope: "company",
        templateId: null,
        companySectionKey,
        disciplines,
        category,
      });
      onCreated(result.resource);
      onOpenChange(false);
    } catch (err) {
      const message = (err as { message?: string } | null)?.message;
      toast({
        title: "Não foi possível criar o recurso",
        description: message,
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-resource-dialog">
        <DialogHeader>
          <DialogTitle>Novo recurso</DialogTitle>
          <DialogDescription>
            O recurso será criado no pool e ficará disponível no picker.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-resource-title">Título</Label>
            <Input
              id="create-resource-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Boas-vindas, Configuração das ferramentas..."
              autoFocus
              data-testid="create-resource-title"
            />
            {titleError && <p className="text-xs text-destructive">Título é obrigatório.</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-resource-kind">Tipo</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as ResourceKind)}>
              <SelectTrigger id="create-resource-kind" data-testid="create-resource-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="link">{KIND_CONFIG.link.label}</SelectItem>
                <SelectItem value="notion">{KIND_CONFIG.notion.label}</SelectItem>
                <SelectItem value="todo">{KIND_CONFIG.todo.label}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {needsUrl && (
            <div className="space-y-2">
              <Label htmlFor="create-resource-url">
                {kind === "notion" ? "URL da página Notion" : "URL"}
              </Label>
              <Input
                id="create-resource-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                data-testid="create-resource-url"
              />
              {urlError && (
                <p className="text-xs text-destructive">Informe uma URL http(s) válida.</p>
              )}
            </div>
          )}

          {kind === "todo" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="create-resource-todo-description">Descrição (opcional)</Label>
                <Textarea
                  id="create-resource-todo-description"
                  value={todoDescription}
                  onChange={(e) => setTodoDescription(e.target.value)}
                  placeholder="Detalhes sobre o que precisa ser feito"
                  rows={3}
                  data-testid="create-resource-todo-description"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-resource-todo-link">Link (opcional)</Label>
                <Input
                  id="create-resource-todo-link"
                  type="url"
                  value={todoLink}
                  onChange={(e) => setTodoLink(e.target.value)}
                  placeholder="https://..."
                  data-testid="create-resource-todo-link"
                />
                {todoLinkError && (
                  <p className="text-xs text-destructive">Informe uma URL http(s) válida.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-resource-todo-due">Prazo (opcional)</Label>
                <Select value={dueRule} onValueChange={(v) => setDueRule(v as DueRule | "")}>
                  <SelectTrigger
                    id="create-resource-todo-due"
                    data-testid="create-resource-todo-due"
                  >
                    <SelectValue placeholder="No dia do milestone (padrão)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day_of">No dia do milestone</SelectItem>
                    <SelectItem value="next_day">Dia seguinte</SelectItem>
                    <SelectItem value="this_week">Na semana</SelectItem>
                    <SelectItem value="specific_date">Data específica</SelectItem>
                    <SelectItem value="no_deadline">Sem prazo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {dueRule === "specific_date" && (
                <div className="space-y-2">
                  <Label htmlFor="create-resource-todo-due-date">Data</Label>
                  <Input
                    id="create-resource-todo-due-date"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    data-testid="create-resource-todo-due-date"
                  />
                  {dueDateError && <p className="text-xs text-destructive">Informe uma data.</p>}
                </div>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label>Categoria</Label>
            <div className="rounded-md border border-border overflow-hidden">
              <ul className="divide-y divide-border">
                {CATEGORY_OPTIONS.map((opt) => {
                  const selected = opt.value === category;
                  return (
                    <li key={opt.value}>
                      <button
                        type="button"
                        onClick={() => setCategory(selected ? null : opt.value)}
                        className={
                          "w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors " +
                          (selected
                            ? "bg-[#feecee] font-medium text-foreground"
                            : "hover:bg-secondary text-foreground")
                        }
                        data-testid={`create-resource-category-${opt.value}`}
                      >
                        <span
                          className={
                            "inline-flex h-4 w-4 items-center justify-center rounded-full border " +
                            (selected ? "border-[#ef4d56]" : "border-border")
                          }
                          aria-hidden
                        >
                          {selected ? <span className="h-2 w-2 rounded-full bg-[#ef4d56]" /> : null}
                        </span>
                        <span>{opt.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <p className="text-xs text-muted-foreground">
              Categorias são labels de orientação. Todo conteúdo fica visível para todos.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              data-testid="create-resource-submit"
            >
              {createMutation.isPending ? "Criando..." : "Criar recurso"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
